/**
 * APP-ROUTE-ERROR-BOUNDARY-V1 -- RouteErrorBoundary: the route-level
 * render-error containment wrapper. Before this package the frontend had
 * ZERO error boundaries (verified across refs; the #601 crash class: one
 * malformed child payload unmounted the ENTIRE page up to the root). This
 * class boundary catches a child's RENDER-PHASE throw and keeps the
 * failure inside the routed region, so the authenticated shell (top bar +
 * nav) survives and the user can navigate away.
 *
 * WHAT IT CATCHES / WHAT IT DOES NOT (React contract, stated honestly):
 * render-phase errors (plus lifecycle/constructor errors of children)
 * ONLY. It does NOT catch event-handler errors, async/promise rejections,
 * setTimeout/async-effect callbacks, or SSR errors. The #601 class WAS
 * render-phase; this boundary contains that class, nothing more.
 *
 * WHY A CLASS COMPONENT: the app mounts the DECLARATIVE router
 * (frontend/src/main.tsx <BrowserRouter> + frontend/src/App.tsx <Routes>),
 * where react-router's errorElement does nothing -- errorElement is a DATA
 * router (createBrowserRouter) feature. React 18 has no hook equivalent of
 * getDerivedStateFromError/componentDidCatch, so a class is the ONE lawful
 * minimal mechanism. NO router migration here (charter fence).
 *
 * FALLBACK VOCABULARY (COMPETITOR FENCE honored): the fallback renders the
 * EXISTING ErrorState primitive (03 P11: retry affordance, never a blank
 * hole). NEITHER existing error component was designed for a thrown render
 * error -- DashboardLoadError owns status-driven 404/401/403 LOAD/auth
 * cards (untouched here) and ErrorState owns in-flow section failures;
 * ErrorState is the closer fit (in-flow, no HTTP classification) and is
 * REUSED, not forked. No detail line is passed: a raw JS error message is
 * an internal, and internals never surface (LES-009 error-passthrough law;
 * componentDidCatch logs it to the console for operators instead).
 *
 * RESET LAW (the trapped-user rule): a boundary that catches a crash but
 * strands the user on a dead page has not solved the problem. On any
 * pathname change while crashed, componentDidUpdate clears the error so
 * the next route renders normally; the Try again affordance re-attempts
 * the same route in place. Healthy children render as a PASS-THROUGH
 * (zero wrapper markup), so adopting this boundary is render-invisible
 * until a crash actually happens.
 *
 * Presentation only, never a security boundary (CLINICAL WIRE LAW /
 * doctrine 06): it grants no role any door or field -- server fences
 * remain the authority; the fallback is role-neutral.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import ErrorState from './ErrorState';
import AcButton from './AcButton';

export interface RouteErrorBoundaryFallbackProps {
  /** Re-attempt rendering the crashed region in place. */
  readonly onRetry: () => void;
}

/**
 * The fallback view (exported for the node-env render pin): the EXISTING
 * ErrorState primitive with the P11-required retry affordance. Role-neutral
 * by construction -- no detail line, no role-shaped door (LES-016: the nav
 * that survives beside this card is the lawful way out).
 */
export function RouteErrorBoundaryFallback({
  onRetry,
}: RouteErrorBoundaryFallbackProps): JSX.Element {
  return (
    <ErrorState
      title="Could not display this screen"
      action={
        <AcButton variant="secondary" onClick={onRetry}>
          Try again
        </AcButton>
      }
      testId="route-error-boundary"
    />
  );
}

interface ResettingErrorBoundaryProps {
  /** Reset signal: a DIFFERENT value while crashed clears the error state
   *  (the default export feeds the current pathname, so navigating to
   *  another route always escapes the fallback). */
  readonly resetKey: string;
  readonly children: ReactNode;
}

interface ResettingErrorBoundaryState {
  readonly crashed: boolean;
}

/**
 * The class boundary (exported for the node-env lifecycle pins; mount the
 * default export, which wires resetKey to the router location).
 */
export class ResettingErrorBoundary extends Component<
  ResettingErrorBoundaryProps,
  ResettingErrorBoundaryState
> {
  state: ResettingErrorBoundaryState = { crashed: false };

  static getDerivedStateFromError(): ResettingErrorBoundaryState {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Operator signal only (production React swallows caught errors
    // silently); the raw message stays in the console, never in the UI.
    console.error(
      '[RouteErrorBoundary] contained a render error at',
      this.props.resetKey,
      error,
      info.componentStack,
    );
  }

  componentDidUpdate(prevProps: ResettingErrorBoundaryProps): void {
    if (this.state.crashed && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ crashed: false });
    }
  }

  render(): ReactNode {
    if (this.state.crashed) {
      return (
        <RouteErrorBoundaryFallback
          onRetry={() => this.setState({ crashed: false })}
        />
      );
    }
    return this.props.children;
  }
}

/**
 * Mount this around routed content (the DashboardLayout outlet, the
 * AppLayout outlet, the app root). Reads the router location so a crashed
 * region self-heals on navigation -- children are NEVER keyed or remounted
 * by this wrapper on normal navigation; only the crashed flag resets.
 */
export default function RouteErrorBoundary({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const location = useLocation();
  return (
    <ResettingErrorBoundary resetKey={location.pathname}>
      {children}
    </ResettingErrorBoundary>
  );
}
